/**
 * /eleve/progression — Ma progression (Phase 31 refonte)
 *
 * Auparavant : cette page renvoyait un `BoEleveDashboardPage` qui affichait
 * "Ton compte n'est pas encore lié" pour tout élève sans fiche secrétariat
 * → inutile pour la majorité des utilisateurs code2roues (candidats libres,
 * apprentissage autonome, etc.).
 *
 * Maintenant : affichage toujours utile centré sur le LIVRET REMC (0/38) et
 * l'auto-évaluation. Les stats secrétariat (moniteur, heures) sont ajoutées
 * en bonus SI l'élève est effectivement rattaché.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, BookOpen, ClipboardCheck, ArrowRight, Loader2, Award,
} from 'lucide-react';
import api from '../../api';

const COMPETENCES = [
  { code: 'C1', label: 'Maîtrise du véhicule', total: 12 },
  { code: 'C2', label: 'Circulation', total: 8 },
  { code: 'C3', label: 'Conditions difficiles', total: 11 },
  { code: 'C4', label: 'Autonomie', total: 7 },
];

const TOTAL_ITEMS = 38;

export default function ElevePregressionPage() {
  const [prog, setProg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Ma progression · Espace élève';
    (async () => {
      try {
        const p = await api.getLivretProgression();
        setProg(p);
      } catch (_) {
        setProg({ items: [] });
      }
      setLoading(false);
    })();
  }, []);

  // Le backend renvoie déjà summary.by_competence (dict) et summary.acquis (int)
  const summary = prog?.summary || { acquis: 0, percent: 0, by_competence: {} };
  const byCompetence = COMPETENCES.map((c) => {
    const compSummary = (summary.by_competence || {})[c.code] || { acquis: 0, total: c.total };
    return { ...c, validated: compSummary.acquis || 0, total: compSummary.total || c.total };
  });
  const totalValidated = summary.acquis || 0;
  const globalPct = Math.round(summary.percent || 0);

  return (
    <div className="max-w-4xl" data-testid="eleve-progression-page">
      <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-rose-500 mb-2">Élève · Progression</div>
      <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        Ma progression
      </h1>
      <p className="text-sm text-white/60 mt-2 mb-6">
        Suis l&apos;acquisition des 38 sous-compétences du livret REMC moto.
      </p>

      {loading && (
        <div className="text-center py-12 text-white/40">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          <div className="text-sm">Chargement…</div>
        </div>
      )}

      {!loading && (
        <>
          {/* ─── Bloc synthèse globale ────────────────────────────── */}
          <div className="rounded-2xl border border-rose-500/30 bg-gradient-to-br from-rose-500/[0.06] to-transparent p-6 mb-6" data-testid="global-progress-card">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1">Progression REMC</div>
                <div className="text-4xl sm:text-5xl font-black text-white leading-none" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {totalValidated}<span className="text-white/40">/{TOTAL_ITEMS}</span>
                </div>
                <div className="text-white/60 text-[13px] mt-1">sous-compétences validées</div>
              </div>
              <div className="text-right">
                <div className="text-5xl sm:text-6xl font-black leading-none" style={{ fontFamily: "'Playfair Display', Georgia, serif", color: globalPct >= 80 ? '#10b981' : globalPct >= 40 ? '#f59e0b' : '#e11d48' }}>
                  {globalPct}<span className="text-2xl">%</span>
                </div>
              </div>
            </div>
            <div className="h-2 rounded-full bg-black/40 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-rose-500 to-rose-400 transition-all" style={{ width: `${globalPct}%` }} />
            </div>
          </div>

          {/* ─── Détail par compétence ────────────────────────────── */}
          <div className="space-y-2 mb-6" data-testid="competences-list">
            {byCompetence.map((c) => {
              const pct = c.total ? Math.round((c.validated / c.total) * 100) : 0;
              return (
                <Link
                  key={c.code}
                  to={`/livret#${c.code}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 transition p-4 group"
                  data-testid={`competence-${c.code}`}
                >
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-rose-500/15 text-rose-300 border border-rose-500/25 text-[13px] font-black">
                        {c.code}
                      </span>
                      <span className="text-white font-semibold truncate">{c.label}</span>
                    </div>
                    <div className="text-white/70 text-[13px] font-semibold whitespace-nowrap">
                      {c.validated}/{c.total}
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-black/40 overflow-hidden">
                    <div className="h-full bg-rose-500 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </Link>
              );
            })}
          </div>

          {/* ─── CTA principaux ───────────────────────────────────── */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Link
              to="/livret"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="btn-open-livret"
              className="rounded-2xl border border-rose-500/30 bg-rose-600 hover:bg-rose-700 p-5 flex items-center gap-3 shadow-lg shadow-rose-900/30 transition group"
            >
              <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <BookOpen size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-black text-[15px]">Continuer mon livret</div>
                <div className="text-white/80 text-[12px] mt-0.5">Ouvre le livret REMC complet</div>
              </div>
              <ArrowRight size={18} className="text-white/80 group-hover:translate-x-0.5 transition-transform shrink-0" />
            </Link>

            <Link
              to="/mon-livret-moto"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="btn-open-autoeval"
              className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] hover:bg-emerald-500/[0.12] p-5 flex items-center gap-3 transition group"
            >
              <div className="w-11 h-11 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
                <ClipboardCheck size={20} className="text-emerald-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-black text-[15px]">Auto-évaluation</div>
                <div className="text-white/60 text-[12px] mt-0.5">Note tes compétences acquises</div>
              </div>
              <ArrowRight size={18} className="text-emerald-300 group-hover:translate-x-0.5 transition-transform shrink-0" />
            </Link>
          </div>

          {/* ─── Encouragement contextuel ─────────────────────────── */}
          {globalPct === 0 && (
            <div className="mt-6 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 flex items-start gap-3">
              <Award className="text-amber-400 shrink-0 mt-0.5" size={18} />
              <div className="text-[13px] text-white/70">
                <strong className="text-white">Commence par ton auto-évaluation.</strong> Passe en revue les 38 sous-compétences et coche celles que tu maîtrises déjà — la progression se met à jour automatiquement.
              </div>
            </div>
          )}

          {globalPct > 0 && globalPct < 100 && (
            <div className="mt-6 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4 flex items-start gap-3">
              <TrendingUp className="text-emerald-400 shrink-0 mt-0.5" size={18} />
              <div className="text-[13px] text-white/70">
                <strong className="text-white">Belle progression !</strong> {TOTAL_ITEMS - totalValidated} sous-compétence{(TOTAL_ITEMS - totalValidated) > 1 ? 's' : ''} à valider pour compléter ton livret REMC.
              </div>
            </div>
          )}

          {globalPct === 100 && (
            <div className="mt-6 rounded-xl border border-emerald-500/40 bg-emerald-500/[0.08] p-4 flex items-start gap-3">
              <Award className="text-emerald-300 shrink-0 mt-0.5" size={18} />
              <div className="text-[13px] text-white">
                <strong>Bravo !</strong> Toutes les compétences REMC sont validées — tu es prêt pour ton examen 🎉
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
